import React, { useState, useRef } from 'react';
import {
  FiImage, FiZap, FiSearch, FiUpload, FiTrash2, FiCheck, FiScissors, FiChevronLeft, FiChevronRight,
  FiGitMerge, FiRefreshCw, FiExternalLink, FiEdit3, FiX,
} from 'react-icons/fi';
import { fullStoryService } from '../../services/apiService';
import { useT } from '../../utils/i18n';
import { StepCard, Notice, formatSeconds, showError, clipPlan, VIDEO_UPLOAD_MAX_MB } from './shared';
import { photoAge } from '../../utils/photoAge';

const GAP_SEC = 0.3; // pause after each sentence — matches the server

/** Step 3 — one picture per scene; scenes are runs of sentences the user can split, merge and re-tag. */
export const ImagesStep = ({ story, run, readOnly }) => {
  const t = useT();
  const id = story._id;
  const sentences = story.voice.sentences;
  const indexOf = new Map(sentences.map((s, i) => [s._id, i]));
  const ranges = story.scenes.map((sc) => {
    const idx = sc.sentenceIds.map((sid) => indexOf.get(sid)).filter((n) => n !== undefined);
    return { sceneId: sc._id, first: Math.min(...idx), last: Math.max(...idx) };
  });
  const missing = story.scenes.filter((s) => !s.image?.filename).length;
  const planning = story.images.status === 'planning';
  const generating = story.scenes.filter((s) => s.status === 'generating').length;
  const [instructions, setInstructions] = useState(story.images.instructions || '');

  const saveLayout = (layout, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    run(() => fullStoryService.setLayout(id, layout));
  };

  const layoutOps = {
    split: (i, at) => saveLayout([
      ...ranges.slice(0, i),
      { ...ranges[i], last: at - 1 },
      { sceneId: null, first: at, last: ranges[i].last },
      ...ranges.slice(i + 1),
    ]),
    merge: (i) => {
      const target = i > 0 ? i - 1 : 1;
      const merged = i > 0
        ? { ...ranges[i - 1], last: ranges[i].last }
        : { ...ranges[1], first: ranges[0].first };
      const next = ranges.filter((_, k) => k !== i && k !== target);
      next.splice(Math.min(i, target), 0, merged);
      saveLayout(next, story.scenes[i].image?.filename ? t('fullStory.images.mergeConfirm') : null);
    },
    giveFirstToPrevious: (i) => {
      if (ranges[i].first === ranges[i].last) return layoutOps.merge(i);
      saveLayout(ranges.map((r, k) => (k === i - 1 ? { ...r, last: r.last + 1 } : k === i ? { ...r, first: r.first + 1 } : r)));
    },
    giveLastToNext: (i) => {
      if (ranges[i].first === ranges[i].last) return layoutOps.merge(i);
      saveLayout(ranges.map((r, k) => (k === i + 1 ? { ...r, first: r.first - 1 } : k === i ? { ...r, last: r.last - 1 } : r)));
    },
  };

  return (
    <StepCard
      title={t('fullStory.images.title')}
      description={t('fullStory.images.desc')}
      footer={!readOnly && (
        <>
          <p className="text-xs text-gray-500">
            {missing ? t('fullStory.images.missing', { count: missing }) : story.images.approved ? t('fullStory.images.approvedNote') : t('fullStory.images.approveHint')}
          </p>
          <button
            onClick={() => run(() => fullStoryService.approveImages(id))}
            disabled={!story.scenes.length || missing > 0 || planning || generating > 0}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <FiCheck size={15} /> {t('fullStory.images.approve')}
          </button>
        </>
      )}
    >
      {!story.voice.approved && <Notice tone="warn">{t('fullStory.images.needVoice')}</Notice>}

      {story.voice.approved && (
        <PictureInstructions
          story={story}
          instructions={instructions}
          setInstructions={setInstructions}
          readOnly={readOnly}
          busy={planning || generating > 0}
          run={run}
          t={t}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600">
          {planning
            ? t('fullStory.images.planning')
            : t('fullStory.images.summary', { scenes: story.scenes.length, sentences: sentences.length })}
          {generating > 0 && <span className="text-gray-400"> · {t('fullStory.images.makingCount', { count: generating })}</span>}
        </p>
        {!readOnly && story.voice.approved && !planning && story.scenes.length > 0 && (
          <button
            onClick={() => { if (window.confirm(t('fullStory.images.replanConfirm'))) run(() => fullStoryService.planScenes(id, { instructions })); }}
            className="btn btn-ghost text-sm flex items-center gap-1.5"
          >
            <FiRefreshCw size={13} /> {t('fullStory.images.replan')}
          </button>
        )}
      </div>
      {story.images.status === 'failed' && <Notice tone="error">{story.images.error}</Notice>}

      {planning && (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600">
          <span className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          {t('fullStory.images.planningLong')}
        </div>
      )}

      <ol className="space-y-4">
        {story.scenes.map((scene, i) => (
          <SceneCard
            key={scene._id}
            story={story}
            scene={scene}
            index={i}
            range={ranges[i]}
            isLast={i === story.scenes.length - 1}
            readOnly={readOnly}
            run={run}
            ops={layoutOps}
            t={t}
          />
        ))}
      </ol>
    </StepCard>
  );
};

export const MAX_PICTURE_INSTRUCTIONS = 500;

/** Scenes "Remake pictures" replaces — AI pictures and scenes without one (mirrors the server). */
export const remakeTargets = (scenes) => scenes.filter((s) => !s.image?.filename || s.image.source === 'ai');

/**
 * Optional instructions for every picture ("all bikes must be Royal Enfield
 * Classic 350"). Before the pictures exist they're used by "Make pictures";
 * afterwards "Remake pictures" makes new AI pictures that follow them.
 */
const PictureInstructions = ({ story, instructions, setInstructions, readOnly, busy, run, t }) => {
  const id = story._id;
  const saved = story.images.instructions || '';
  const hasScenes = story.scenes.length > 0;
  const targets = remakeTargets(story.scenes).length;
  const kept = story.scenes.length - targets;

  if (readOnly) {
    if (!saved) return null;
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
        <p className="text-xs font-semibold text-gray-500 mb-1">{t('fullStory.images.instructionsLabel')}</p>
        <p className="text-sm text-gray-700 whitespace-pre-wrap">{saved}</p>
      </div>
    );
  }

  const makePictures = () => run(() => fullStoryService.planScenes(id, { instructions }));
  const remake = () => {
    const message = kept > 0
      ? t('fullStory.images.remakeConfirmKeep', { count: targets, kept })
      : t('fullStory.images.remakeConfirm', { count: targets });
    if (window.confirm(message)) run(() => fullStoryService.remakePictures(id, { instructions }));
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
      <label htmlFor="picture-instructions" className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
        <FiEdit3 size={13} /> {t('fullStory.images.instructionsLabel')}
        <span className="font-normal text-gray-400">{t('fullStory.images.optional')}</span>
      </label>
      <textarea
        id="picture-instructions"
        value={instructions}
        onChange={(e) => setInstructions(e.target.value.slice(0, MAX_PICTURE_INSTRUCTIONS))}
        maxLength={MAX_PICTURE_INSTRUCTIONS}
        rows={3}
        disabled={busy}
        placeholder={t('fullStory.images.instructionsPlaceholder')}
        className="input w-full text-sm leading-relaxed disabled:opacity-60"
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-gray-400">
          {t('fullStory.images.instructionsHint')} · <span className="tabular-nums">{instructions.length}/{MAX_PICTURE_INSTRUCTIONS}</span>
        </p>
        {hasScenes ? (
          <button
            type="button"
            onClick={remake}
            disabled={busy || targets === 0}
            title={targets === 0 ? t('fullStory.images.remakeNothing') : undefined}
            className="btn btn-ghost text-xs py-1.5 px-3 flex items-center gap-1.5 disabled:opacity-50"
          >
            <FiRefreshCw size={12} /> {t('fullStory.images.remake', { count: targets })}
          </button>
        ) : (
          <button type="button" onClick={makePictures} disabled={busy} className="btn btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50">
            <FiZap size={13} /> {t('fullStory.images.makePictures')}
          </button>
        )}
      </div>
    </div>
  );
};

const SOURCE_BADGE = {
  ai: 'fullStory.images.sourceAi',
  web: 'fullStory.images.sourceWeb',
  upload: 'fullStory.images.sourceUpload',
  stock: 'fullStory.images.sourceStock',
};
const VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'];
const isVideoFile = (file) => VIDEO_TYPES.includes(file.type) || /\.(mp4|mov|m4v|webm)$/i.test(file.name || '');
const oneDecimal = (n) => (Math.round(n * 10) / 10).toString();

/**
 * How this scene's video clip fills it, in words, with a "Start at" box when
 * the clip is longer than the scene.
 */
const ClipPlan = ({ story, scene, seconds, readOnly, run, t }) => {
  const image = scene.image;
  const [start, setStart] = useState(String(image.clipStart || 0));
  const plan = clipPlan(seconds, image.durationSec, image.clipStart || 0);
  const longer = (image.durationSec || 0) > seconds + 0.05;
  const latest = Math.max(0, (image.durationSec || 0) - 0.5);
  const save = () => {
    const value = Number(start);
    if (!Number.isFinite(value) || value < 0) { setStart(String(image.clipStart || 0)); return; }
    if (value === (image.clipStart || 0)) return;
    run(() => fullStoryService.updateScene(story._id, scene._id, { clipStart: value }));
  };
  let text;
  if (plan.stretch === 1) {
    text = t('fullStory.images.clipPlays', { used: formatSeconds(seconds), clip: oneDecimal(image.durationSec || 0), from: oneDecimal(image.clipStart || 0) });
  } else if (plan.frozenSec < 0.05) {
    text = t('fullStory.images.clipSlowed', { clip: oneDecimal(plan.available), scene: oneDecimal(seconds), speed: Math.round(100 / plan.stretch) });
  } else {
    text = t('fullStory.images.clipSlowedHold', {
      clip: oneDecimal(plan.available), scene: oneDecimal(seconds), speed: Math.round(100 / plan.stretch),
      played: oneDecimal(plan.playedSec), hold: oneDecimal(plan.frozenSec),
    });
  }
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
      <p className="text-xs text-gray-600">{text}</p>
      {plan.frozenSec >= 0.05 && <p className="text-[11px] text-gray-400">{t('fullStory.images.clipHoldTip')}</p>}
      {longer && !readOnly && (
        <label className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
          {t('fullStory.images.clipStartAt')}
          <input
            type="number"
            min={0}
            max={latest}
            step={0.1}
            value={start}
            onChange={(e) => setStart(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            className="input w-20 text-xs py-1"
          />
          <span className="text-gray-400">{t('fullStory.images.clipStartHint', { max: oneDecimal(latest) })}</span>
        </label>
      )}
    </div>
  );
};

const SceneCard = ({ story, scene, index, range, isLast, readOnly, run, ops, t }) => {
  const id = story._id;
  const [panel, setPanel] = useState(null); // null | 'brief' | 'web'
  const [brief, setBrief] = useState(scene.prompt || '');
  const [query, setQuery] = useState(scene.photoQuery || '');
  const [searching, setSearching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const sentences = story.voice.sentences.slice(range.first, range.last + 1);
  const seconds = sentences.reduce((sum, s, k) => sum + (s.durationSec || 0) + (range.first + k < story.voice.sentences.length - 1 ? GAP_SEC : 0), 0);
  const busy = scene.status === 'generating' || uploading;
  const isVideo = scene.image?.kind === 'video';
  const portrait = story.aspectRatio === '9:16';

  const search = async () => {
    setSearching(true);
    await run(() => fullStoryService.searchScenePhotos(id, scene._id, query));
    setSearching(false);
  };

  const upload = async (file) => {
    if (!file) return;
    if (!isVideoFile(file)) {
      await run(() => fullStoryService.uploadSceneImage(id, scene._id, file));
      return;
    }
    if (file.size > VIDEO_UPLOAD_MAX_MB * 1024 * 1024) {
      showError(null, t('fullStory.images.videoTooBig', { max: VIDEO_UPLOAD_MAX_MB }));
      return;
    }
    setUploading(true);
    try {
      await run(() => fullStoryService.uploadSceneVideo(id, scene._id, file));
    } finally {
      setUploading(false);
    }
  };

  return (
    <li className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
        <p className="text-sm font-semibold text-gray-800">
          {t('fullStory.images.scene', { n: index + 1 })}
          <span className="font-normal text-gray-500"> · {formatSeconds(seconds)} · {range.first === range.last
            ? t('fullStory.images.sentence', { n: range.first + 1 })
            : t('fullStory.images.sentences', { from: range.first + 1, to: range.last + 1 })}</span>
        </p>
        {!readOnly && (
          <div className="flex items-center gap-0.5">
            {index > 0 && (
              <button onClick={() => ops.giveFirstToPrevious(index)} title={t('fullStory.images.giveFirst')} className="p-1.5 rounded-lg text-gray-400 hover:bg-white hover:text-gray-700"><FiChevronLeft size={15} /></button>
            )}
            {!isLast && (
              <button onClick={() => ops.giveLastToNext(index)} title={t('fullStory.images.giveLast')} className="p-1.5 rounded-lg text-gray-400 hover:bg-white hover:text-gray-700"><FiChevronRight size={15} /></button>
            )}
            {story.scenes.length > 1 && (
              <button onClick={() => ops.merge(index)} title={index > 0 ? t('fullStory.images.mergePrev') : t('fullStory.images.mergeNext')} className="p-1.5 rounded-lg text-gray-400 hover:bg-white hover:text-gray-700"><FiGitMerge size={14} /></button>
            )}
          </div>
        )}
      </div>

      <div className={`grid gap-4 p-4 ${portrait ? 'sm:grid-cols-[200px_1fr]' : 'md:grid-cols-[minmax(0,360px)_1fr]'}`}>
        {/* Picture */}
        <div>
          <div className={`relative rounded-xl overflow-hidden bg-gray-100 ${portrait ? 'aspect-[9/16] max-w-[200px]' : 'aspect-video'}`}>
            {scene.image?.filename && isVideo ? (
              <video
                key={scene.image.filename}
                src={fullStoryService.fileUrl(id, scene.image.filename)}
                poster={scene.image.poster ? fullStoryService.fileUrl(id, scene.image.poster) : undefined}
                controls
                muted
                playsInline
                preload="none"
                className="absolute inset-0 w-full h-full object-cover bg-black"
              />
            ) : scene.image?.filename ? (
              <img src={fullStoryService.fileUrl(id, scene.image.filename)} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-gray-400 text-xs">
                <FiImage size={22} />
                {!busy && t('fullStory.images.noPicture')}
              </div>
            )}
            {busy && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/70 text-xs text-gray-600">
                <span className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                {uploading ? t('fullStory.images.uploadingVideo') : t('fullStory.images.making')}
              </div>
            )}
            {scene.image?.source && SOURCE_BADGE[scene.image.source] && (
              <span className={`absolute ${isVideo ? 'top-1.5' : 'bottom-1.5'} left-1.5 rounded bg-black/60 text-white text-[10px] font-medium px-1.5 py-0.5 pointer-events-none`}>
                {t(isVideo && scene.image.source === 'upload' ? 'fullStory.images.sourceUploadVideo' : SOURCE_BADGE[scene.image.source])}
                {isVideo && ` · ${t('fullStory.images.videoBadge', { seconds: oneDecimal(scene.image.durationSec || 0) })}`}
              </span>
            )}
          </div>
          {isVideo && scene.image?.source === 'stock' && scene.image.author && (
            <p className="mt-1 text-[11px] text-gray-500 truncate">{t('fullStory.images.stockCredit', { author: scene.image.author, provider: scene.image.provider || '' })}</p>
          )}
          {scene.image?.source === 'web' && scene.image.pageUrl && (
            <a href={scene.image.pageUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-primary-600 hover:underline max-w-full truncate">
              <FiExternalLink size={10} /> {scene.image.site || scene.image.pageUrl}{scene.image.publishedAt ? ` · ${photoAge(scene.image.publishedAt)}` : ''}
            </a>
          )}
          {scene.status === 'failed' && scene.error && <p className="mt-1 text-xs text-danger-600">{scene.error}</p>}
        </div>

        {/* Sentences + actions */}
        <div className="min-w-0 space-y-3">
          <ol className="space-y-1">
            {sentences.map((s, k) => (
              <li key={s._id} className="group flex items-start gap-2">
                <span className="flex-shrink-0 w-5 text-right text-[11px] font-semibold text-gray-400 mt-0.5 tabular-nums">{range.first + k + 1}</span>
                <p className="flex-1 text-sm leading-relaxed text-gray-700" style={{ fontFamily: '"Noto Sans Telugu", sans-serif' }}>{s.text}</p>
                {!readOnly && k > 0 && (
                  <button
                    onClick={() => ops.split(index, range.first + k)}
                    title={t('fullStory.images.splitHere')}
                    className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-primary-600 hover:bg-primary-50 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
                  >
                    <FiScissors size={13} />
                  </button>
                )}
              </li>
            ))}
          </ol>

          {isVideo && scene.image?.durationSec > 0 && (
            <ClipPlan key={`${scene.image.filename}-${scene.image.clipStart || 0}`} story={story} scene={scene} seconds={seconds} readOnly={readOnly} run={run} t={t} />
          )}

          {scene.photoQuery && !scene.image?.filename?.length && (
            <p className="text-xs text-gray-500">{t('fullStory.images.photoSuggestion', { query: scene.photoQuery })}</p>
          )}

          {!readOnly && (
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setPanel(panel === 'brief' ? null : 'brief')} disabled={busy} className="btn btn-ghost text-xs py-1.5 px-2.5 flex items-center gap-1.5 disabled:opacity-40">
                <FiZap size={12} /> {t('fullStory.images.aiPicture')}
              </button>
              <button onClick={() => setPanel(panel === 'web' ? null : 'web')} disabled={busy} className="btn btn-ghost text-xs py-1.5 px-2.5 flex items-center gap-1.5 disabled:opacity-40">
                <FiSearch size={12} /> {t('fullStory.images.webPhotos')}
              </button>
              <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn btn-ghost text-xs py-1.5 px-2.5 flex items-center gap-1.5 disabled:opacity-40">
                <FiUpload size={12} /> {t('fullStory.images.upload')}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm,.mov,.m4v"
                className="hidden"
                onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }}
              />
              {scene.image?.filename && (
                <button
                  onClick={() => { if (window.confirm(t('fullStory.images.removeConfirm'))) run(() => fullStoryService.removeSceneImage(id, scene._id)); }}
                  disabled={busy}
                  className="btn btn-ghost text-xs py-1.5 px-2.5 flex items-center gap-1.5 text-danger-600 hover:bg-danger-50 disabled:opacity-40"
                >
                  <FiTrash2 size={12} /> {t('fullStory.images.remove')}
                </button>
              )}
            </div>
          )}

          {panel === 'brief' && !readOnly && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500">
                <FiEdit3 size={12} /> {t('fullStory.images.briefLabel')}
              </label>
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder={t('fullStory.images.briefPlaceholder')}
                className="input w-full text-sm leading-relaxed"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setPanel(null)} className="btn btn-ghost text-xs py-1.5 px-3"><FiX size={12} /></button>
                <button
                  onClick={() => { setPanel(null); run(() => fullStoryService.generateSceneImage(id, scene._id, { prompt: brief })); }}
                  className="btn btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
                >
                  <FiZap size={12} /> {scene.image?.filename ? t('fullStory.images.makeNew') : t('fullStory.images.make')}
                </button>
              </div>
              <p className="text-[11px] text-gray-400">{t('fullStory.images.aiCost')}</p>
            </div>
          )}

          {panel === 'web' && !readOnly && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-3">
              <div className="flex gap-2">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && query.trim()) search(); }}
                  placeholder={t('fullStory.images.searchPlaceholder')}
                  className="input flex-1 text-sm"
                />
                <button onClick={search} disabled={!query.trim() || searching} className="btn btn-primary text-xs px-3 flex items-center gap-1.5 disabled:opacity-50">
                  {searching ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiSearch size={12} />}
                  {t('fullStory.images.search')}
                </button>
              </div>
              {scene.webOptions?.length > 0 ? (
                <div className={`grid gap-2 ${portrait ? 'grid-cols-3 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`}>
                  {scene.webOptions.map((o) => (
                    <button
                      key={o.filename}
                      onClick={() => { setPanel(null); run(() => fullStoryService.chooseScenePhoto(id, scene._id, o.filename)); }}
                      title={o.title || o.site}
                      className="group relative aspect-video rounded-lg overflow-hidden bg-gray-200 ring-primary-400 hover:ring-2"
                    >
                      <img src={fullStoryService.fileUrl(id, o.filename)} alt={o.title || ''} className="absolute inset-0 w-full h-full object-cover" />
                      <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate text-left">{o.site}{o.publishedAt ? ` · ${photoAge(o.publishedAt)}` : ''}</span>
                    </button>
                  ))}
                </div>
              ) : !searching && <p className="text-xs text-gray-500">{t('fullStory.images.searchHint')}</p>}
              <p className="text-[11px] text-gray-400">{t('fullStory.images.webRights')}</p>
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

